import { Button, EmptyState, ErrorState, Kbd, Markdown, Skeleton, Tag, Tooltip } from '@pm/ui';
import { ArrowLeft, ChevronRight, FileQuestion, Lock, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { ApiRequestError } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useDeleteNote, useNote, useSaveNote } from '@/lib/notes';
import { useToast } from '@/lib/toast';
import { useBackToNotes } from './use-back-to-notes';

/** Leitura de uma anotação: Markdown formatado; "Editar" (atalho E) abre o editor. */
export function NotePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const back = useBackToNotes();
  const { data: note, isLoading, isError, error, refetch } = useNote(id);
  const del = useDeleteNote();
  const saveNote = useSaveNote();
  const [confirm, setConfirm] = useState(false);
  const edit = () => void navigate(`/anotacoes/${id}/editar`);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]')) return;
      if (e.key.toLowerCase() === 'e' && !e.metaKey && !e.ctrlKey && !e.altKey && note) {
        e.preventDefault();
        edit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note]);

  const notFound = isError && error instanceof ApiRequestError && error.status === 404;

  return (
    <div className="flex max-w-[960px] flex-col gap-gap-lg">
      <div className="flex items-center gap-2 font-mono text-xs text-text-3">
        <Button variant="secondary" size="sm" className="mr-1 font-sans" onClick={back}>
          <ArrowLeft /> Voltar
        </Button>
        <Lock className="size-3" />
        <Link to="/anotacoes" className="hover:text-text-1">
          Minhas anotações
        </Link>
        {note ? (
          <>
            <ChevronRight className="size-3" />
            <span className="truncate">{note.title}</span>
          </>
        ) : null}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : notFound ? (
        <EmptyState
          icon={FileQuestion}
          title="Anotação não encontrada"
          description="Ela pode ter sido excluída."
          action={
            <Button size="sm" onClick={() => void navigate('/anotacoes')}>
              Ver minhas anotações
            </Button>
          }
        />
      ) : isError || !note ? (
        <ErrorState message={error?.message} onRetry={() => void refetch()} />
      ) : (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-semibold leading-tight [overflow-wrap:anywhere]">
                {note.title}
              </h1>
              <p className="mt-1 font-mono text-xs text-text-3">
                criada {fmtDateTime(note.created_at)}
                {note.updated_at !== note.created_at
                  ? ` · alterada ${fmtDateTime(note.updated_at)}`
                  : null}
              </p>
              {note.tags.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {note.tags.map((t) => (
                    <Link key={t} to={`/anotacoes?tag=${encodeURIComponent(t)}`}>
                      <Tag className="hover:border-border-strong">{t}</Tag>
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-gap">
              <Button variant="ghost" onClick={() => setConfirm(true)} aria-label="Excluir">
                <Trash2 /> Excluir
              </Button>
              <Tooltip
                content={
                  <>
                    Editar <Kbd>E</Kbd>
                  </>
                }
              >
                <Button variant="primary" onClick={edit}>
                  <Pencil /> Editar
                </Button>
              </Tooltip>
            </div>
          </div>

          <article className="rounded-md border border-border bg-surface-1 px-pad-x py-4 sm:px-6 sm:py-5">
            {note.content_markdown.trim() ? (
              <Markdown
                className="[overflow-wrap:anywhere]"
                onChange={(next) =>
                  saveNote.mutate(
                    { id: note.id, body: { content_markdown: next } },
                    { onError: (err) => toast.error(err) },
                  )
                }
              >
                {note.content_markdown}
              </Markdown>
            ) : (
              <p className="text-sm text-text-3">
                Sem conteúdo ainda. Use <span className="text-text-2">Editar</span> para escrever.
              </p>
            )}
          </article>

          <ConfirmDialog
            open={confirm}
            onOpenChange={setConfirm}
            title="Excluir anotação?"
            description={
              <p className="text-sm text-text-2">
                <span className="text-text-1">{note.title}</span> sai da sua lista.
              </p>
            }
            confirmLabel="Excluir"
            danger
            loading={del.isPending}
            onConfirm={() =>
              del.mutate(note.id, {
                onSuccess: () => {
                  toast.success('Anotação excluída');
                  void navigate('/anotacoes', { replace: true });
                },
                onError: (err) => toast.error(err),
              })
            }
          />
        </>
      )}
    </div>
  );
}
